import React, { useState, useEffect, useRef } from 'react';

const SearchableDropdown = ({ options, value, onChange, placeholder, name, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                // If the current search term is not exactly one of the options, 
                // you might want to clear it or keep it as custom value depending on requirements.
                // For now, we will keep it as is, effectively allowing custom values if the parent component supports it
                // or the user can just leave it as valid text.
                // However, if we want to enforce selection, we could reset to `value` prop here.
                if (value && searchTerm !== value) {
                    setSearchTerm(value);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchTerm, value]);

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);

        const filtered = options.filter(option =>
            option.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredOptions(filtered);

        // Propagate change to parent immediately allows typing
        onChange({ target: { name, value: term } });
    };

    const handleSelectOption = (option) => {
        setSearchTerm(option);
        onChange({ target: { name, value: option } });
        setIsOpen(false);
    };

    const handleFocus = () => {
        setIsOpen(true);
        // Reset filter when opening to show all or relevant options
        setFilteredOptions(
            options.filter(option =>
                option.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                name={name}
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleFocus}
                placeholder={placeholder}
                className={className}
                autoComplete="off"
            />
            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.map((option, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleSelectOption(option)}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 transition text-sm text-gray-700"
                        >
                            {option}
                        </button>
                    ))}
                </div>
            )}
            {isOpen && filteredOptions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    <div className="px-4 py-3 text-sm text-gray-500">
                        No matches found
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
